import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { authService } from '@/services/authService';
import { useAuth } from '@/contexts/AuthContext';
import { defaultHomePath } from '@/utils/access';
import toast from 'react-hot-toast';

const passwordPolicy = [
  'At least 8 characters',
  'At least one uppercase letter',
  'At least one lowercase letter',
  'At least one number',
  'At least one special character',
];

const ChangePassword: React.FC = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const mustChangePassword = Boolean(user?.mustChangePassword);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      if (mustChangePassword) {
        await authService.forceChangePassword(newPassword);
      } else {
        await authService.changePassword({
          oldPassword: currentPassword,
          newPassword,
        });
      }
      if (user) {
        updateUser({ ...user, mustChangePassword: false });
      }
      toast.success('Password changed successfully!');
      navigate(mustChangePassword ? defaultHomePath(user) : '/profile');
    } catch (error: any) {
      toast.error(error.response?.data?.message || error.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Change Password - HRMS</title>
      </Helmet>
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-4 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-6 sm:space-y-8">
          <div className="text-center">
            <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-2">
              Change Your Password
            </h1>
            <p className="text-sm sm:text-base text-gray-600">
              {mustChangePassword
                ? 'Please set a new password to continue'
                : 'Enter your current password and choose a new one'}
            </p>
          </div>
          <div className="bg-white py-6 sm:py-8 px-4 sm:px-6 shadow-lg rounded-lg">
            <form className="space-y-4 sm:space-y-6" onSubmit={handleSubmit}>
              {!mustChangePassword && (
                <div>
                  <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-1">
                    Current Password
                  </label>
                  <input
                    id="currentPassword"
                    name="currentPassword"
                    type="password"
                    autoComplete="current-password"
                    required
                    className="input w-full"
                    placeholder="Enter current password"
                    value={currentPassword}
                    onChange={e => setCurrentPassword(e.target.value)}
                  />
                </div>
              )}
              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  New Password
                </label>
                <input
                  id="newPassword"
                  name="newPassword"
                  type="password"
                  autoComplete="new-password"
                  required
                  className="input w-full"
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm New Password
                </label>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  required
                  className="input w-full"
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                />
              </div>
              <div className="text-xs text-gray-500 mb-2">
                <p className="font-semibold mb-1">Password must contain:</p>
                <ul className="list-disc list-inside">
                  {passwordPolicy.map(rule => (
                    <li key={rule}>{rule}</li>
                  ))}
                </ul>
              </div>
              <div className="pt-2">
                <button
                  type="submit"
                  className="btn btn-primary w-full py-3 text-sm sm:text-base"
                  disabled={loading}
                >
                  {loading ? 'Changing...' : 'Change Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default ChangePassword;
